import { useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { usarTrilha } from '../lib/trilha.ts';
import { Icone } from './Icone.tsx';

/**
 * ⭐ **A barra superior da área de trabalho — sistema visual 2026, 14/09/2026.**
 *
 * Diz onde se está e que dia é. É fina de propósito: o menu lateral leva a
 * navegação, e a altura da janela fica para o trabalho.
 *
 * ⚠️ **A data vem do SERVIDOR**, como todo "hoje" desta casa (`CLAUDE.md`):
 * o relógio do navegador erra de fuso e de ajuste, e a data que aparece aqui
 * é a mesma que decide prazo e atraso. Sem resposta, a data simplesmente não
 * aparece — nunca a do computador no lugar.
 */

const SECOES: Array<[string, string]> = [
  ['/clientes', 'Clientes'],
  ['/calendario', 'Calendário'],
  ['/financeiro', 'Financeiro'],
  ['/relatorios', 'Relatórios'],
  /* ⚠️ Faltava: a barra dizia "KairósHub" em cima da aba Comercial. */
  ['/comercial', 'Comercial'],
  ['/marketing', 'Marketing'],
];

function secaoDoCaminho(caminho: string): string {
  if (caminho === '/') return 'Hoje';
  return SECOES.find(([prefixo]) => caminho.startsWith(prefixo))?.[1] ?? 'KairósHub';
}

const DIAS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];
const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/** `2026-09-14` → `SEG · 14 SET 2026`. Conta sobre a data pura, sem fuso. */
function dataDaBarra(iso: string): string {
  const [a, m, d] = iso.split('-').map(Number);
  const dia = new Date(Date.UTC(a!, m! - 1, d!)).getUTCDay();
  return `${DIAS[dia]} · ${String(d).padStart(2, '0')} ${MESES[m! - 1]} ${a}`;
}

/**
 * ⭐⭐ **A HORA DE SÃO PAULO — pedido dele, 21/09/2026.**
 *
 * ⛔⛔ **É a hora de SÃO PAULO, não a do navegador — e a diferença não é
 * preciosismo.** A data ao lado vem do servidor, e o Hub inteiro conta o dia
 * pelo fuso do escritório. **Hora do navegador com data do servidor seriam duas
 * fontes na mesma linha:** quem abrisse o Hub de outro fuso veria o dia de São
 * Paulo com a hora do lugar onde está, e a linha diria duas verdades diferentes
 * sobre o mesmo instante.
 *
 * ⚠️ **O relógio do computador pode estar errado, e o fuso não corrige isso.**
 * Isto converte o instante da máquina para São Paulo; se a máquina estiver
 * adiantada, a barra fica adiantada junto. **A data continua vindo do
 * servidor**, que é o que decide prazo — e é por isso que ela não se calcula
 * daqui.
 */
const RELOGIO = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

export function BarraSuperior({
  caminho,
  aoNavegar,
  menuRecolhido,
  aoMostrarMenu,
}: {
  caminho: string;
  aoNavegar: (caminho: string) => void;
  menuRecolhido: boolean;
  /** Com o menu recolhido, a barra oferece o caminho de volta sem depender do mouse na borda. */
  aoMostrarMenu: () => void;
}) {
  const [hoje, setHoje] = useState<string | null>(null);
  const [agora, setAgora] = useState(() => RELOGIO.format(new Date()));
  const trilha = usarTrilha();

  /*
   * ⚠️ **Um segundo de intervalo, e o relógio para quando a aba sai de vista.**
   * Sem a segunda parte, dez abas abertas ficariam acordando o navegador uma
   * vez por segundo cada uma, para desenhar um número que ninguém está vendo.
   */
  useEffect(() => {
    let id: number | undefined;
    const ligar = () => {
      if (id !== undefined) return;
      setAgora(RELOGIO.format(new Date()));
      id = window.setInterval(() => setAgora(RELOGIO.format(new Date())), 1000);
    };
    const desligar = () => {
      if (id !== undefined) window.clearInterval(id);
      id = undefined;
    };
    const aoTrocarDeAba = () => (document.hidden ? desligar() : ligar());
    ligar();
    document.addEventListener('visibilitychange', aoTrocarDeAba);
    return () => {
      desligar();
      document.removeEventListener('visibilitychange', aoTrocarDeAba);
    };
  }, []);

  useEffect(() => {
    let vivo = true;
    api
      .painel()
      .then((p) => vivo && setHoje(p.hoje))
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, []);

  return (
    <header className="barra">
      {menuRecolhido && (
        <button
          type="button"
          className="barra-mostrar-menu"
          onClick={aoMostrarMenu}
          aria-label="Mostrar o menu"
          title="Mostrar o menu"
        >
          <Icone nome="menu-fixar" tamanho={17} />
        </button>
      )}
      {trilha.length > 0 ? (
        <nav className="barra-trilha" aria-label="Onde se está">
          {trilha.map((p, i) => (
            <span key={`${p.rotulo}-${i}`} className="barra-passo">
              {i > 0 && <span className="barra-sep" aria-hidden="true">/</span>}
              {p.caminho ? (
                <button type="button" onClick={() => aoNavegar(p.caminho!)}>
                  {p.rotulo}
                </button>
              ) : (
                <span aria-current="page">{p.rotulo}</span>
              )}
            </span>
          ))}
        </nav>
      ) : (
        <p className="barra-titulo">{secaoDoCaminho(caminho)}</p>
      )}
      <div className="barra-direita">
        {hoje && (
          <span className="barra-data">
            {dataDaBarra(hoje)} · <span className="barra-hora">{agora}</span>
          </span>
        )}
      </div>
    </header>
  );
}
